import { locallySignedOut } from '@/lib/local-session';
import { StrictMode, type ReactNode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';

import { AuthLifecycle } from '@/components/auth/auth-lifecycle';
import { AuthModeProvider } from '@/components/auth/auth-mode';
import { AuthProvider } from '@/components/auth/auth-provider';
import { UserProvider } from '@/components/auth/user-provider';
import { EntityCacheBoundary } from '@/hooks/entity-cache-boundary';
import { ThemeProvider } from '@/components/theme/theme-provider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { buildMsalConfig, isAuthConfigured } from '@/lib/auth-config';
import '@fontsource/lato/400.css';
import '@fontsource/lato/400-italic.css';
import '@fontsource/lato/700.css';
import '@fontsource/lato/900.css';
import { App } from './App';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

function AppTree({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <BrowserRouter>
            {children}
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

// Preserve the root and MSAL instance when Vite re-evaluates this entry.
// Recreating either during hot updates can duplicate session lifecycles.
const runtime: { root?: Root; msal?: PublicClientApplication } = import.meta.hot?.data.authRuntime ?? {};
if (import.meta.hot) import.meta.hot.data.authRuntime = runtime;

async function bootstrap() {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Root element #root not found');
  }

  const authEnabled = isAuthConfigured();

  if (authEnabled) {
    // Singleton PCA: initialize before React mounts so redirect responses are
    // hydrated once and MsalProvider can listen continuously.
    const msalInstance = runtime.msal ??= new PublicClientApplication(buildMsalConfig());
    await msalInstance.initialize();

    const accounts = msalInstance.getAllAccounts();
    if (!locallySignedOut() && accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0] ?? null);
    }

    (runtime.root ??= createRoot(rootEl)).render(
      <StrictMode>
        <AppTree>
          <AuthModeProvider enabled>
            <MsalProvider instance={msalInstance}>
              <AuthLifecycle />
              <AuthProvider>
                <UserProvider>
                  <EntityCacheBoundary><App /></EntityCacheBoundary>
                </UserProvider>
              </AuthProvider>
            </MsalProvider>
          </AuthModeProvider>
        </AppTree>
      </StrictMode>
    );
    return;
  }

  (runtime.root ??= createRoot(rootEl)).render(
    <StrictMode>
      <AppTree>
        <AuthModeProvider enabled={false}>
          <AuthProvider>
            <UserProvider>
              <EntityCacheBoundary><App /></EntityCacheBoundary>
            </UserProvider>
          </AuthProvider>
        </AuthModeProvider>
      </AppTree>
    </StrictMode>
  );
}

void bootstrap();

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // The app remains usable online when offline support cannot be installed.
    });
  });
}
